import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  endUploadWatch,
  UPLOAD_WATCH_POLL_MS,
  UPLOAD_WATCH_TIMEOUT_MS,
} from "@/features/invoices/uploadWatchSlice";
import { REVIEW_STATUS } from "@/lib/statusEnums";
import {
  useGetInvoicesQuery,
  useGetMyInvoicesQuery,
  useGetStarredInvoicesQuery,
} from "@/services/invoices/invoicesApi";

const NO_GUIDS = [];
const EMPTY = { items: [], pagination: { page: 1, pageSize: 0, totalPages: 0, total: 0 } };

/**
 * Picks the endpoint for a queue. All three hooks have to be called
 * unconditionally — hooks cannot be called in a branch — so the two that do not
 * apply are skipped rather than omitted.
 *
 * After an upload the list polls (see `uploadWatchSlice`) only until the uploaded
 * invoice has appeared and finished extracting, then stops.
 */
export function useInvoiceList(source, args, { skip = false } = {}) {
  const dispatch = useDispatch();
  const startedAt = useSelector((state) => state.uploadWatch.startedAt);
  const watchedGuids = useSelector((state) => state.uploadWatch.guids ?? NO_GUIDS);
  const watching = startedAt !== null;

  const options = (skipQuery) => ({
    skip: skip || skipQuery,
    pollingInterval: watching && !skip ? UPLOAD_WATCH_POLL_MS : 0,
  });
  const all = useGetInvoicesQuery(args, options(source !== "all"));
  const mine = useGetMyInvoicesQuery(args, options(source !== "my"));
  const starred = useGetStarredInvoicesQuery(args, options(source !== "starred"));

  const active = source === "my" ? mine : source === "starred" ? starred : all;
  const items = active.data?.items;

  // Stop once every uploaded invoice is on the loaded page and past "New"
  // (extraction done). The page is already in memory, so the whole of it is
  // checked: sorting or filters can put an invoice anywhere on it.
  useEffect(() => {
    if (!watching || !items) return;
    const extracted = (guid) => {
      const invoice = items.find((item) => item?.guid === guid);
      const statusId = invoice?.reviewStatusId;
      return statusId != null && statusId !== REVIEW_STATUS.NEW;
    };
    if (watchedGuids.every(extracted)) dispatch(endUploadWatch());
  }, [watching, items, watchedGuids, dispatch]);

  // An invoice that never appears (filtered out, another page, failed upload)
  // must not poll forever.
  useEffect(() => {
    if (!watching) return undefined;
    const remaining = Math.max(0, startedAt + UPLOAD_WATCH_TIMEOUT_MS - Date.now());
    const timer = setTimeout(() => dispatch(endUploadWatch()), remaining);
    return () => clearTimeout(timer);
  }, [watching, startedAt, dispatch]);

  if (skip) {
    return {
      ...active,
      invoices: EMPTY.items,
      pagination: {
        page: 1,
        pageSize: args?.pageSize ?? 0,
        totalPages: 0,
        total: 0,
      },
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: undefined,
    };
  }

  return {
    ...active,
    invoices: items ?? EMPTY.items,
    pagination: active.data?.pagination ?? EMPTY.pagination,
  };
}
